import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { SlidersHorizontal, X } from "lucide-react";
import { API, usePageTitle, useShop } from "@/context/ShopContext";
import ProductCard from "@/components/ProductCard";
import { FABRIC_INFO } from "@/lib/fabricInfo";

const SORTS = [
  { value: "featured", label: "Featured" },
  { value: "newest", label: "Newest" },
  { value: "price_asc", label: "Price — Low to High" },
  { value: "price_desc", label: "Price — High to Low" },
  { value: "az", label: "A–Z" },
];

const PRICES = [
  { value: "", label: "All" },
  { value: "everyday", label: "Under ₹450" },
  { value: "classic", label: "₹450 – ₹499" },
  { value: "premium", label: "₹500+" },
];

export default function Shop() {
  const { meta } = useShop();
  const [searchParams] = useSearchParams();
  const category = searchParams.get("category") || "hijabs";
  const catInfo = (meta.categories || []).find((c) => c.id === category);
  usePageTitle(category === "hijabs" ? "Shop Hijabs" : catInfo?.label || "Shop");

  const [shade, setShade] = useState(searchParams.get("shade") || "");
  const [collection, setCollection] = useState(searchParams.get("collection") || "");
  const [families, setFamilies] = useState(() => (searchParams.get("family") ? [searchParams.get("family")] : []));
  const [fabrics, setFabrics] = useState(() => (searchParams.get("fabric") ? [searchParams.get("fabric")] : []));
  const [price, setPrice] = useState("");
  const [occasion, setOccasion] = useState(searchParams.get("occasion") || "");
  const [inStock, setInStock] = useState(false);
  const [sort, setSort] = useState("featured");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filtersOpen, setFiltersOpen] = useState(false);

  useEffect(() => {
    setShade(searchParams.get("shade") || "");
    if (searchParams.get("collection")) {
      setCollection(searchParams.get("collection"));
      if (!searchParams.get("family")) setFamilies([]);
      if (!searchParams.get("fabric")) setFabrics([]);
    }
    if (searchParams.get("family")) {
      setFamilies([searchParams.get("family")]);
      if (!searchParams.get("fabric")) setFabrics([]);
      if (!searchParams.get("collection")) setCollection("");
    }
    if (searchParams.get("fabric")) {
      setFabrics([searchParams.get("fabric")]);
      if (!searchParams.get("family")) setFamilies([]);
      if (!searchParams.get("collection")) setCollection("");
    }
  }, [searchParams]);

  const query = useMemo(() => {
    const p = new URLSearchParams();
    if (shade) p.set("shade", shade);
    if (collection) p.set("collection", collection);
    if (families.length) p.set("family", families.join(","));
    if (fabrics.length) p.set("fabric", fabrics.join(","));
    if (price === "everyday") p.set("max_price", "449");
    if (price === "classic") { p.set("min_price", "450"); p.set("max_price", "499"); }
    if (price === "premium") p.set("min_price", "500");
    if (occasion) p.set("occasion", occasion);
    if (inStock) p.set("availability", "in_stock");
    p.set("sort", sort);
    return p.toString();
  }, [shade, collection, families, fabrics, price, occasion, inStock, sort]);

  useEffect(() => {
    if (category !== "hijabs") return;
    setLoading(true);
    fetch(`${API}/products?${query}`)
      .then((r) => r.json())
      .then((d) => setResults(d.products || []))
      .catch(() => setResults([]))
      .finally(() => setLoading(false));
  }, [query, category]);

  const shadeGroups = useMemo(() => {
    const order = (meta.families || []).map((f) => f.name);
    const src = families.length ? (meta.shades || []).filter((s) => families.includes(s.family)) : meta.shades || [];
    const groups = {};
    src.forEach((s) => {
      (groups[s.family] = groups[s.family] || []).push(s);
    });
    return order.filter((f) => groups[f]?.length).map((f) => ({ family: f, shades: groups[f] }));
  }, [families, meta.shades, meta.families]);

  const toggle = (list, setList, value) =>
    setList(list.includes(value) ? list.filter((v) => v !== value) : [...list, value]);

  const clearAll = () => {
    setShade(""); setCollection(""); setFamilies([]); setFabrics([]); setPrice(""); setOccasion(""); setInStock(false);
  };

  const hasFilters = shade || collection || families.length || fabrics.length || price || occasion || inStock;
  const activeFabricInfo = fabrics.length === 1 ? FABRIC_INFO.find((f) => f.name === fabrics[0]) : null;
  const activeCollection = collection ? (meta.collections || []).find((c) => c.slug === collection || c.name === collection) : null;

  if (category !== "hijabs") {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-8 py-28 md:py-40 text-center" data-testid="coming-soon-page">
        <p className="overline-label mb-6">Coming Soon</p>
        <p className="font-arabic text-4xl text-rose" lang="ar" dir="rtl">{catInfo?.arabic || "لمسة"}</p>
        <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl font-light tracking-tight mt-4">
          LAMSAH {catInfo?.label || "Collection"}
        </h1>
        {catInfo?.image && (
          <div className="max-w-md mx-auto mt-10 overflow-hidden bg-beige">
            <img src={catInfo.image} alt={`LAMSAH ${catInfo.label} — coming soon`} className="w-full h-auto" data-testid="coming-soon-image" />
          </div>
        )}
        <p className="text-sm sm:text-base text-espresso/70 leading-relaxed mt-6 max-w-md mx-auto">
          {catInfo?.tagline || "This collection is being lovingly prepared."}
        </p>
        <Link to="/shop" className="btn-primary mt-10" data-testid="coming-soon-shop-link">
          Shop the Hijab Catalogue
        </Link>
      </div>
    );
  }

  const filterPanel = (
    <div className="space-y-10">
      <div>
        <p className="overline-label mb-5">Collection</p>
        <div className="space-y-3">
          {(meta.collections || []).map((c) => (
            <label key={c.slug} className="flex items-center gap-3 cursor-pointer group">
              <input
                type="radio"
                name="collection"
                data-testid={`filter-collection-${c.slug}`}
                checked={collection === c.slug}
                onChange={() => setCollection(collection === c.slug ? "" : c.slug)}
                onClick={() => collection === c.slug && setCollection("")}
                className="w-4 h-4 accent-[#2B211C]"
              />
              <span className="text-sm text-espresso/80 group-hover:text-espresso transition-colors">{c.name}</span>
              <span className="text-xs text-taupe">({c.count})</span>
            </label>
          ))}
        </div>
      </div>
      <div>
        <p className="overline-label mb-5">Colour Family</p>
        <div className="space-y-3">
          {(meta.families || []).map((f) => (
            <label key={f.name} className="flex items-center gap-3 cursor-pointer group">
              <input
                type="checkbox"
                data-testid={`filter-family-${f.name.toLowerCase().replace(/\s+/g, "-")}`}
                checked={families.includes(f.name)}
                onChange={() => toggle(families, setFamilies, f.name)}
                className="w-4 h-4 accent-[#2B211C]"
              />
              <span className="w-4 h-4 rounded-full border border-beige" style={{ backgroundColor: f.hex }} />
              <span className="text-sm text-espresso/80 group-hover:text-espresso transition-colors">{f.name}</span>
            </label>
          ))}
        </div>
      </div>
      <div>
        <p className="overline-label mb-5">Shade</p>
        <div className="max-h-64 overflow-y-auto pr-1 space-y-5">
          {shadeGroups.map((g) => (
            <div key={g.family}>
              <p className="text-[10px] uppercase tracking-[0.2em] text-taupe mb-2.5">{g.family}</p>
              <div className="grid grid-cols-6 gap-2.5">
                {g.shades.map((s, k) => (
                  <button
                    key={`${s.collection}-${s.name}-${k}`}
                    data-testid={`filter-shade-${s.name.toLowerCase().replace(/\s+/g, "-")}`}
                    aria-label={`Filter by ${s.name}`}
                    title={`${s.name} · ${s.collection}`}
                    onClick={() => {
                      if (shade === s.name) {
                        setShade("");
                      } else {
                        setCollection("");
                        setFabrics([]);
                        setShade(s.name);
                      }
                    }}
                    className={`w-7 h-7 rounded-full border border-white ring-1 ring-offset-2 ring-offset-ivory transition-all duration-300 ${
                      shade === s.name ? "ring-espresso" : "ring-transparent hover:ring-taupe"
                    }`}
                    style={{ backgroundColor: s.hex }}
                  />
                ))}
              </div>
            </div>
          ))}
        </div>
        {shade && (
          <p className="text-xs text-taupe mt-3">
            Shade: <span className="text-espresso">{shade}</span>
          </p>
        )}
      </div>
      <div>
        <p className="overline-label mb-5">Fabric</p>
        <div className="space-y-3">
          {(meta.fabrics || []).map((f) => {
            const count = meta.fabric_counts?.[f] || 0;
            return (
              <label key={f} className="flex items-center gap-3 cursor-pointer group">
                <input
                  type="checkbox"
                  data-testid={`filter-fabric-${f.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "")}`}
                  checked={fabrics.includes(f)}
                  onChange={() => toggle(fabrics, setFabrics, f)}
                  className="w-4 h-4 accent-[#2B211C]"
                />
                <span className="text-sm text-espresso/80 group-hover:text-espresso transition-colors">{f}</span>
                <span className="text-xs text-taupe">({count})</span>
              </label>
            );
          })}
        </div>
      </div>
      <div>
        <p className="overline-label mb-5">Style</p>
        <div className="space-y-3">
          {[{ value: "", label: "All Styles" }, { value: "everyday", label: "Everyday Wear" }, { value: "occasion", label: "Special Occasion" }].map((o) => (
            <label key={o.value || "all"} className="flex items-center gap-3 cursor-pointer group">
              <input
                type="radio"
                name="occasion"
                data-testid={`filter-occasion-${o.value || "all"}`}
                checked={occasion === o.value}
                onChange={() => setOccasion(o.value)}
                className="w-4 h-4 accent-[#2B211C]"
              />
              <span className="text-sm text-espresso/80 group-hover:text-espresso transition-colors">{o.label}</span>
            </label>
          ))}
        </div>
      </div>
      <div>
        <p className="overline-label mb-5">Price</p>
        <div className="space-y-3">
          {PRICES.map((pr) => (
            <label key={pr.value} className="flex items-center gap-3 cursor-pointer group">
              <input
                type="radio"
                name="price"
                data-testid={`filter-price-${pr.value || "all"}`}
                checked={price === pr.value}
                onChange={() => setPrice(pr.value)}
                className="w-4 h-4 accent-[#2B211C]"
              />
              <span className="text-sm text-espresso/80 group-hover:text-espresso transition-colors">{pr.label}</span>
            </label>
          ))}
        </div>
      </div>
      <div>
        <p className="overline-label mb-5">Availability</p>
        <label className="flex items-center gap-3 cursor-pointer group">
          <input
            type="checkbox"
            data-testid="filter-in-stock"
            checked={inStock}
            onChange={() => setInStock(!inStock)}
            className="w-4 h-4 accent-[#2B211C]"
          />
          <span className="text-sm text-espresso/80 group-hover:text-espresso transition-colors">In stock only</span>
        </label>
      </div>
      {hasFilters && (
        <button data-testid="clear-filters" onClick={clearAll} className="text-xs uppercase tracking-[0.2em] text-rose border-b border-rose/40 pb-0.5">
          Clear all
        </button>
      )}
    </div>
  );

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-8 py-14 md:py-20" data-testid="shop-page">
      <header className="mb-12 md:mb-16">
        <p className="overline-label mb-4">Every Shade. Your Style.</p>
        <h1 className="font-serif text-4xl sm:text-5xl font-light tracking-tight">
          {activeCollection
            ? `${activeCollection.name.toUpperCase()} — ${activeCollection.fabric.toUpperCase()}`
            : fabrics.length === 1
            ? `${fabrics[0].toUpperCase()} HIJABS`
            : families.length === 1
            ? `${families[0].toUpperCase()} HIJABS`
            : "SHOP HIJABS"}
        </h1>
        <p className="text-sm sm:text-base text-espresso/70 mt-4 max-w-xl">
          {activeCollection ? activeCollection.story : activeFabricInfo ? activeFabricInfo.intro : "Discover your everyday signature."}
        </p>
        <div className="flex flex-wrap gap-2 mt-6" data-testid="fabric-family-chips">
          {(meta.families || []).map((f) => (
              <button
                key={f.name}
                data-testid={`chip-family-${f.name.toLowerCase().replace(/\s+/g, "-")}`}
                onClick={() => {
                  setFabrics([]);
                  setCollection("");
                  setShade("");
                  setFamilies(families.includes(f.name) ? [] : [f.name]);
                }}
                className={`inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.15em] px-3.5 py-2 border transition-colors duration-300 ${
                  families.includes(f.name) ? "border-espresso text-espresso bg-white" : "border-beige text-taupe hover:border-taupe"
                }`}
              >
                <span className="w-3 h-3 rounded-full border border-beige" style={{ backgroundColor: f.hex }} />
                {f.name}
              </button>
            ))}
          </div>
      </header>

      <div className="flex items-center justify-between gap-4 mb-8 md:hidden">
        <button
          data-testid="mobile-filters-toggle"
          onClick={() => setFiltersOpen(!filtersOpen)}
          className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] border border-espresso px-5 py-3"
        >
          {filtersOpen ? <X className="w-4 h-4" strokeWidth={1.5} /> : <SlidersHorizontal className="w-4 h-4" strokeWidth={1.5} />}
          Filters
        </button>
        <select
          data-testid="sort-select-mobile"
          value={sort}
          onChange={(e) => setSort(e.target.value)}
          className="input-lux !w-auto text-xs uppercase tracking-[0.15em]"
          aria-label="Sort products"
        >
          {SORTS.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
        </select>
      </div>
      {filtersOpen && <div className="md:hidden mb-10 p-6 bg-white border border-beige">{filterPanel}</div>}

      <div className="flex gap-14">
        <aside className="hidden md:block w-60 shrink-0">
          <div className="sticky top-28">{filterPanel}</div>
        </aside>
        <div className="flex-1">
          <div className="hidden md:flex items-center justify-between mb-10">
            <p className="text-xs uppercase tracking-[0.2em] text-taupe" data-testid="results-count">
              {results.length} {results.length === 1 ? "piece" : "pieces"}
            </p>
            <select
              data-testid="sort-select"
              value={sort}
              onChange={(e) => setSort(e.target.value)}
              className="input-lux !w-auto text-xs uppercase tracking-[0.15em]"
              aria-label="Sort products"
            >
              {SORTS.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
            </select>
          </div>
          {loading ? (
            <div className="grid grid-cols-2 lg:grid-cols-3 gap-x-5 gap-y-12">
              {[...Array(6)].map((_, i) => (
                <div key={i} className="animate-pulse">
                  <div className="bg-beige aspect-[4/5]" />
                  <div className="h-4 bg-beige mt-4 w-2/3" />
                  <div className="h-3 bg-beige mt-2 w-1/3" />
                </div>
              ))}
            </div>
          ) : results.length === 0 ? (
            <div className="py-24 text-center" data-testid="shop-empty">
              <p className="font-serif text-2xl">No pieces match your selection</p>
              <p className="text-sm text-espresso/60 mt-3">Try softening a filter or exploring another shade.</p>
              <button data-testid="empty-clear-filters" onClick={clearAll} className="btn-secondary mt-8">Clear Filters</button>
            </div>
          ) : (
            <div className="grid grid-cols-2 lg:grid-cols-3 gap-x-5 gap-y-12" data-testid="product-grid">
              {results.map((p) => <ProductCard key={p.id} product={p} />)}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
